import FolderIcon from "../../assets/icons/svg/folder.svg";
import FileMERIcon from "../../assets/icons/svg/FileMER.svg";
import FallbackIcon from "../../assets/icons/svg/files.svg";
import _React from "react";

// Eager glob only specific patterns
const fileIcons = import.meta.glob("../../assets/icons/svg/File*.svg", {
  eager: true,
  import: "default",
});
const shortcutIcons = import.meta.glob("../../assets/icons/svg/filesShortcutIcons/Sfile*.svg", {
  eager: true,
  import: "default",
});

/** The three fields this picker reads off whatever row/attachment/file object it is handed. */
interface FileIconSource {
  name?: string;
  // `boolean | number`: the transmittal callers pass `AttachedFile.isFolder`, which the schema
  // declares as a number. Every read below is a truthiness test, so both forms behave identically.
  isFolder?: boolean | number;
  resourceType?: string;
}

// The RETURN stays `any`, and is load-bearing: `import.meta.glob` yields an opaque module value that
// consumers use two incompatible ways — as an `<img src>` string (AttachmentsModal, FilesSlickGrid)
// and as a component, `<FileImage />` (MailContent). Narrowing it to either one breaks the other, and
// those consumers are outside this module.
//
// RE-ATTEMPTED (union / generic / overload). The premise above turns out to be wrong, and the real
// finding is a BUG, not a typing gap: the globs below carry no `?react` suffix, so Vite resolves
// these `.svg` imports to URL **strings**. `getFileImage` therefore always returns a string.
//
// That makes AttachmentsModal's `<img src={getFileImage(…)} />` correct, and MailContent's other
// branch dead — it does `const FileImage = getFileImage(…)` then `<FileImage />`, guarded by a
// runtime `typeof FileImage === "string"` check (and an `as unknown as string` on the img branch),
// which reads as the author being unsure which one it was. A `string | ComponentType` union would
// encode that uncertainty rather than resolve it.
//
// The honest return type is `string`. Landing it means deleting MailContent's dead component branch
// — a behaviour change in a file this module does not own, so it is reported rather than applied.
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- returns a URL string; MailContent's component branch is dead code — see comment above
export function getFileImage(file: FileIconSource): any {
  const ext = file.name?.split(".").pop()?.toLowerCase();
  const extUpper = ext?.toUpperCase();

  if (file.isFolder) return FolderIcon;
  if (file.resourceType === "RESOURCE_TYPE_MERGED_FILE") return FileMERIcon;

  if (file.resourceType === "RESOURCE_TYPE_SHORTCUT_FILE") {
    const shortcutPath = `../../assets/icons/svg/filesShortcutIcons/Sfile${extUpper}.svg`;
    return shortcutIcons[shortcutPath] ?? FallbackIcon;
  }

  const dynamicPath = `../../assets/icons/svg/File${extUpper}.svg`;
  return fileIcons[dynamicPath] ?? FallbackIcon;
}
